/**
 * Provides an Axios instance that is already set up with
 * the AccessToken and it builds proxied URLs.
 */

import Axios from "axios";
import { useGetContext } from "@onefront/forrestjs-react-root";
import { buildUrl } from "../utils/build-url";
import { paramsSerializer } from "../utils/params-serializer";

export const useAxios = (hookConfig = {}) => {
  const proxyBaseUrl = useGetContext("one.axios.proxy.baseUrl");
  const accessTokenHeaderKey = useGetContext("one.rest.header.accessToken.key");

  const hasAuthToken = (receivedConfig = {}, overrideConfig = {}) =>
    (hookConfig && hookConfig?.headers?.hasOwnProperty(accessTokenHeaderKey)) ||
    (receivedConfig &&
      receivedConfig?.headers?.hasOwnProperty(accessTokenHeaderKey)) ||
    (overrideConfig &&
      overrideConfig?.headers?.hasOwnProperty(accessTokenHeaderKey));

  const buildConfig = (receivedConfig = {}, overrideConfig = {}) => ({
    withCredentials: !hasAuthToken(receivedConfig, overrideConfig),
    ...hookConfig,
    ...receivedConfig,
    headers: {
      ...(hookConfig.headers || {}),
      ...(receivedConfig.headers || {}),
      ...(overrideConfig.headers || {})
    },
    paramsSerializer:
      hookConfig.paramsSerializer ||
      receivedConfig.paramsSerializer ||
      paramsSerializer,
    ...overrideConfig
  });

  // TODO: Could this be cached in a ProxyContextWrapper?
  const axios = Axios.create(buildConfig());
  const _request = axios.request;
  const _requestAs = (method) => (url, config) =>
    _request(
      buildConfig(config, {
        url: buildUrl({ target: url, proxyBaseUrl }),
        method
      })
    );
  const _dataRequestAs = (method) => (url, data, config) =>
    _request(
      buildConfig(config, {
        url: buildUrl({ target: url, proxyBaseUrl }),
        method,
        data
      })
    );

  // Rewrite axios's instance methods with exensibility
  axios.request = (a) =>
    _request(
      buildConfig(a, { url: buildUrl({ target: a.url, proxyBaseUrl }) })
    );
  axios.get = _requestAs("get");
  axios.head = _requestAs("head");
  axios.delete = _requestAs("delete");
  axios.post = _dataRequestAs("post");
  axios.put = _dataRequestAs("put");
  axios.patch = _dataRequestAs("patch");

  return {
    buildConfig,
    buildUrl,
    axios
  };
};
